import gql from 'graphql-tag'
import base from '@/mixins/pagination/base'

const CREDENTIALS_PAYLOAD = `meta {
  ... on UserCredentialsFacebookPage {
    credentialUrl: pageUrl
  },
  ... on UserCredentialsSqwizPage {
    credentialUrl: viewUrl
  },
  ... on UserCredentialsInformacijaShop {
    credentialUrl: shopUrl
  },
  ... on UserCredentialsShopamineShop {
    credentialUrl: shopUrl
  },
  ... on UserCredentialsWooCommerceShop {
    credentialUrl: shopUrl
  },
  ... on UserCredentialsShopifyShop {
    credentialUrl: shopUrl
  },
  ... on UserCredentialsTwitterProfile {
    credentialUrl: profileUrl
  },
  ... on UserCredentialsPinterestBoard {
    credentialUrl: boardUrl
  }
  }`

export default {
  data () {
    return {
      runExtraQuery: {},
      limit: 20,

      searchResult: {},
      mediaPlanEvents: {},
      campaigns: {},
      users: {},

      sortBy: null,
      sortDesc: false,

      searchFields: [
        {
          field: 'user',
          label: 'Uporabniki'
        },
        {
          field: 'campaign',
          label: 'Kampanje'
        },
        {
          field: 'media_plan_event',
          label: 'Posti'
        }
      ],
    }
  },

  methods: {
    /**
     * Gives label for search result
     * @returns {String}
     */
    getResultLabel (result) {
      return (result.object.name ||
        result.object.label ||
        (result.object.payload || {}).label ||
        (result.object.advertisable || {}).label)
    },

    /**
     * Refetches all queries
     */
    refetchQueries () {
      for (let q of Object.values(this.$apollo.queries)) {
        q.refetch()
      }
    },

    /**
     * Sets sort parameters
     * @returns {Array}
     */
    sort (key, desc) {
      this.sortDesc = desc
      this.sortBy = key
    }
  },

  computed: {
    /**
     * Tells if data is still loading -- check all apollo queries
     * @returns {Boolean}
     */
    dataLoading () {
      return this.$apollo.loading
    },

    /**
     * Gives available search types
     * @returns {Array}
     */
    getAvailableSearchTypes () {
      return this.searchFields.map(sf => sf.field) || []
    },

    /**
     * Gives available search fields
     * @returns {Array}
     */
    getSearchFields () {
      return this.searchFields
    },

    /**
     * Gives available sort fields in proper form
     * @returns {Array}
     */
    getSortFields () {
      if(this.sortBy) {
        if (this.sortDesc) {
          return [this.sortBy]
        } else {
          return [this.sortBy, 'DESC']
        }
      }
      return []
    },

    /**
     * Gives array of MPE's to be further queried off API
     * @returns {Array}
     */
    getResultMpeIds () {
      return this.getResults.filter(sr => sr.type === 'media_plan_event').map(sr => sr.id)
    },

    /**
     * Gives search results
     * @returns {Array}
     */
    getResults () {
      return this.searchResult.set || []
    },

    /**
     * Gives media plan events queried off the API
     * @returns {Array}
     */
    getMediaPlanEvents () {
      return this.mediaPlanEvents.set || []
    },

    /**
     * Gives campaigns queried off the API
     * @returns {Array}
     */
    getCampaigns () {
      return this.campaigns.set || []
    },

    /**
     * Gives users queried off the API
     * @returns {Array}
     */
    getUsers () {
      return this.users.set || []
    },

    /**
     * Updates results with separately quieried objects
     * @returns {Array}
     */
    getUpdatedObjects () {
      let indexedUpdaters = {}
      // Prepare objects to update queried objects
      /// / MPEs
      indexedUpdaters['media_plan_event'] = {}
      for (let mpe of this.getMediaPlanEvents) {
        indexedUpdaters['media_plan_event'][mpe.id] = { ...mpe }
      }

      /// / Campaigns
      indexedUpdaters['campaign'] = {}
      for (let campaign of this.getCampaigns) {
        indexedUpdaters['campaign'][campaign.id] = { ...campaign }
      }

      /// / Users
      indexedUpdaters['user'] = {}
      for (let user of this.getUsers) {
        indexedUpdaters['user'][user.id] = { ...user }
      }

      let rtr = []
      for (let s of this.getResults) {
        let updater = indexedUpdaters[s.type]
        if (updater && updater[s.id]) {
          let oldO = JSON.parse(JSON.stringify(s))
          let newO = { ...updater[s.id] }

          if (s.type === 'media_plan_event') {
            oldO.object = Object.assign({}, oldO.object, newO)
            rtr.push(oldO)
          } else if (s.type === 'campaign') {
            oldO.object.events = (updater[s.id] || {}).events || []
            rtr.push(oldO)
          } else if (s.type === 'user') {
            oldO.object.credentials = (updater[oldO.id] || {}).credentials || []
            oldO.object.roles = (updater[oldO.id] || {}).roles || []
            rtr.push(oldO)
          }
        } else {
          rtr.push({ ...s })
        }
      }

      return rtr
    },

    /**
     * Groups results
     * @returns {Object}
     */
    getGroupedResoults () {
      let rtr = {}

      for (let resoult of this.getUpdatedObjects) {
        if (!rtr[resoult.type]) {
          rtr[resoult.type] = []
        }

        rtr[resoult.type].push({
          id: resoult.id,
          label: this.getResultLabel(resoult),
          object: resoult.object,
          type: resoult.type
        })
      }

      return rtr
    },

    /**
     * Gives search result groups
     * @returns {Array}
     */
    getGroups () {
      return Object.keys(this.getGroupedResoults)
    },

    /**
     * Passes result data for pagination
     * @returns {Array}
     */
    getData () {
      return this.getGroupedResoults
    },

    /**
     * Gives pagination params for search results
     * @returns {Object}
     */
    getPaginationParams () {
      return this.searchResult.info || {}
    }
  },

  mixins: [
    base
  ],

  watch: {
    getQueryString () {
      this.resetPages()
    }
  },

  // Apollo...
  apollo: {
    searchResult: {
      query: gql`query ($query: String!, $type: [SearchDocType!], $sort: [SearchSortFields!], $limit: Int!, $offset: Int!) {
        searchResult: search(query: $query, type: $type, sort: $sort, limit: $limit, offset: $offset){
          info {
            total,
            limit,
            offset
          },
          set{
            id,
            type,
            label,
            object {
              ... on User {
                label: name,
                id,
                createdAt,
                lockedAt,
              },
              
              ... on Campaign {
                label,
                id,
                status
                expiresAt,
                startsAt,
                campaignType: type
              }

              ... on Advertisable {
                id,
                type,
                label,
                createdAt
              },

              ... on MediaPlanEvent {
                id,
                publishAt,
                link
              }
            }
          }
        }
      }`,
      variables () {
        return {
          query: this.getQueryString,
          type: this.getAvailableSearchTypes,
          sort: this.getSortFields,
          limit: this.getLimit,
          offset: this.getOffset
        }
      },
      debounce: 500,
      fetchPolicy: 'cache-and-network'
    },

    mediaPlanEvents: {
      query: gql`query ($id: [UUID!], $limit: Int!) {
        mediaPlanEvents (id: $id, limit: $limit) {
          set {
            id,
            type,
            status,
            link,
            credentials {
              id,
              type,
              ${CREDENTIALS_PAYLOAD}
            },
            advertisable {
              id,
              label
            }
            payload {
              ... on MediaPlanEventSqwizArticle {
                label: title
              },
              ... on MediaPlanEventSqwizProduct {
                label: title
              },
              ... on MediaPlanEventShopaminePost {
                label: name
              },
              ... on MediaPlanEventShopamineProduct {
                label: name
              },
              ... on MediaPlanEventInformacijaPost {
                label: name
              },
              ... on MediaPlanEventInformacijaProduct {
                label: name
              }
            }
          }
        }
      }`,
      variables () {
        return {
          limit: this.getLimit,
          id: this.getResultMpeIds
        }
      },
      fetchPolicy: 'cache-and-network',
      skip() {
        return !this.runExtraQuery.mediaPlanEvents
      }
    },

    campaigns: {
      query: gql`query ($id: [UUID!]!) {
        campaigns (id: $id, limit: -1) {
          set {
            id,
            events {
              set {
                id,
                credentials {
                  id,
                  type,
                  label,
                  ${CREDENTIALS_PAYLOAD}
                }
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.getQueryCampaigns
        }
      },
      fetchPolicy: 'cache-and-network',
      skip() {
        return !this.runExtraQuery.campaigns
      }
    },

    users: {
      query: gql`query ($id: [UUID!]) {
        users (id: $id, limit: -1) {
          set {
            id,
            roles {
              id
            }
            credentials {
              set {
                id,
                type,
                label,
                ${CREDENTIALS_PAYLOAD}
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.getQueryUsers
        }
      },
      fetchPolicy: 'cache-and-network',
      skip() {
        return !this.runExtraQuery.users
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/pagination/providers/search.js